import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { SOFTWARE_ROLES, getCurrentAppUser, normalizeSoftwareRole } from '../utils/accessControl';
import './NewJobCard.css';

const toMoneyNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = Number(String(value || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => `Tk ${toMoneyNumber(value).toFixed(2)}`;

const normalizeDocumentType = (value) => (String(value || '').toLowerCase() === 'estimate' ? 'Estimate' : 'JobCard');

const getLaborPricingForVehicle = (service, vehicle) => {
  const cc = toMoneyNumber(vehicle?.CC ?? vehicle?.cc);
  const fallbackPrice = service.price_mid_1600_2400 || service.price_car_4c_800_1600 || service.price_large_2500_3200 || service.price_xlarge_3500_5700;
  const fallbackDuration = service.duration_1600_2400 || service.duration_800_1600 || service.duration_2400_3400 || service.duration_3500_5700 || 60;

  if (!cc) {
    return {
      price: fallbackPrice,
      durationMinutes: fallbackDuration,
    };
  }

  if (cc <= 1600) {
    return {
      price: service.price_car_4c_800_1600 || fallbackPrice,
      durationMinutes: service.duration_800_1600 || fallbackDuration,
    };
  }

  if (cc <= 2400) {
    return {
      price: service.price_mid_1600_2400 || fallbackPrice,
      durationMinutes: service.duration_1600_2400 || fallbackDuration,
    };
  }

  if (cc <= 3200) {
    return {
      price: service.price_large_2500_3200 || fallbackPrice,
      durationMinutes: service.duration_2400_3400 || fallbackDuration,
    };
  }

  return {
    price: service.price_xlarge_3500_5700 || fallbackPrice,
    durationMinutes: service.duration_3500_5700 || fallbackDuration,
  };
};

const formatTimerElapsed = (startedAt, tick) => {
  if (!startedAt || startedAt === true) {
    return 'Running';
  }

  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) {
    return 'Running';
  }

  const totalSeconds = Math.max(0, Math.floor((tick - started) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `Running ${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `Running ${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const resolveActiveTimerStart = (timer) => {
  const runningSeconds = Math.max(0, Number(timer?.RunningSeconds || 0));
  if (runningSeconds > 0) {
    return new Date(Date.now() - (runningSeconds * 1000)).toISOString();
  }

  return timer?.StartedAt || true;
};

const formatFileSize = (size) => {
  const numericSize = Number(size || 0);
  if (numericSize >= 1024 * 1024) {
    return `${(numericSize / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (numericSize >= 1024) {
    return `${(numericSize / 1024).toFixed(1)} KB`;
  }
  return `${numericSize} B`;
};

const getDefaultAiMessages = () => ([
  {
    role: 'assistant',
    text: 'Albert Einstein is ready. Tell me the vehicle problem and I will ask diagnosis questions, then suggest likely causes and repair steps from the job card context.',
  },
]);

const restoreActiveTimers = (jobLabor, activeTimers) => {
  const normalizedLabor = (jobLabor || []).map((item) => ({
    ...item,
    employeeId: item.employeeId ? String(item.employeeId) : '',
    fixedRate: Boolean(item.fixedRate),
    taxable: Boolean(item.taxable),
  }));
  const timerRows = {};
  const usedRowIndexes = new Set();

  (activeTimers || []).forEach((timer) => {
    const timerEmployeeId = String(timer.EmployeeId || '');
    const timerLaborId = String(timer.JobCardLaborId || '');
    const isTeamTimer = Boolean(Number(timer.IsTeamTimer || 0));

    let rowIndex = timerLaborId
      ? normalizedLabor.findIndex((item, index) =>
        !usedRowIndexes.has(index) && String(item.JobCardLaborId || '') === timerLaborId
      )
      : -1;

    if (rowIndex === -1 && timerEmployeeId) {
      rowIndex = normalizedLabor.findIndex((item, index) =>
        !usedRowIndexes.has(index) && String(item.employeeId || '') === timerEmployeeId
      );
    }

    if (rowIndex === -1 && timerEmployeeId) {
      rowIndex = normalizedLabor.findIndex((item, index) => !usedRowIndexes.has(index) && !item.employeeId);
      if (rowIndex !== -1) {
        normalizedLabor[rowIndex] = {
          ...normalizedLabor[rowIndex],
          employeeId: timerEmployeeId,
        };
      }
    }

    if (rowIndex === -1) {
      rowIndex = normalizedLabor.length;
      normalizedLabor.push({
        laborServiceId: null,
        name: isTeamTimer
          ? (timer.TeamName ? `Active team - ${timer.TeamName}` : 'Active team timer')
          : (timer.EmployeeName ? `Active timer - ${timer.EmployeeName}` : 'Active timer'),
        description: 'Timer restored from running job card time.',
        laborType: 'Service',
        rate: 0,
        hours: 0,
        fixedRate: false,
        taxable: false,
        employeeId: timerEmployeeId,
        teamJobGroupId: timer.TeamJobGroupId ? String(timer.TeamJobGroupId) : '',
        teamCode: timer.TeamCode || '',
        teamName: timer.TeamName || '',
        teamLookup: [timer.TeamCode, timer.TeamName].filter(Boolean).join(' - '),
      });
    }

    usedRowIndexes.add(rowIndex);
    timerRows[rowIndex] = resolveActiveTimerStart(timer);
  });

  return { normalizedLabor, timerRows };
};

const normalizeJobCardDetail = (data) => {
  const {
    jobCard,
    parts: jobParts,
    labor: jobLabor,
    activeTimers,
    activePartIssueRequest,
    activePartIssueItems,
    activeBay,
    attachments,
    aiMessages,
  } = data;
  const { normalizedLabor, timerRows } = restoreActiveTimers(jobLabor, activeTimers);
  const issuedParts = (jobParts || []).map((part) => ({
    ...part,
    taxable: Boolean(part.taxable),
  }));
  const pendingIssueParts = activePartIssueRequest?.Status === 'Pending'
    ? (activePartIssueItems || []).map((part) => ({
      ...part,
      taxable: Boolean(part.taxable),
      submitted: true,
    }))
    : [];
  const normalizedParts = [...issuedParts, ...pendingIssueParts];

  return {
    jobCard,
    parts: normalizedParts,
    labor: normalizedLabor,
    activeLaborTimers: timerRows,
    activePartIssueRequest: activePartIssueRequest || null,
    activeBay: activeBay || null,
    attachments: attachments || [],
    aiMessages: (aiMessages || []).map((item) => ({
      ...item,
      role: item.MessageRole || item.role || 'assistant',
      text: item.MessageText || item.text || '',
    })),
  };
};

function NewJobCard({ onLogout, documentType = 'JobCard' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const currentUser = getCurrentAppUser();
  const isServiceEngineerLogin = normalizeSoftwareRole(currentUser?.softwareRole) === SOFTWARE_ROLES.SERVICE_ENGINEER;
  const currentEmployeeId = currentUser?.employeeId ? String(currentUser.employeeId) : '';
  const isEditMode = Boolean(id);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bays, setBays] = useState([]);
  const [partsInventory, setPartsInventory] = useState([]);
  const [laborServices, setLaborServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedServiceEngineerId, setSelectedServiceEngineerId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
  const [odometerKm, setOdometerKm] = useState('');
  const [selectedBayId, setSelectedBayId] = useState('');
  const [selectedPartId, setSelectedPartId] = useState('');
  const [selectedLaborId, setSelectedLaborId] = useState('');
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const [laborSearchTerm, setLaborSearchTerm] = useState('');
  const [parts, setParts] = useState([]);
  const [labor, setLabor] = useState([]);
  const [createdJob, setCreatedJob] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeLaborTimers, setActiveLaborTimers] = useState({});
  const [timerTick, setTimerTick] = useState(Date.now());
  const [jobCardStatus, setJobCardStatus] = useState('');
  const [activePartIssueRequest, setActivePartIssueRequest] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [currentDocumentType, setCurrentDocumentType] = useState(normalizeDocumentType(documentType));
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [aiDraft, setAiDraft] = useState('');
  const [aiSending, setAiSending] = useState(false);
  const [aiMessages, setAiMessages] = useState(getDefaultAiMessages);
  const [rejectConfirmIndex, setRejectConfirmIndex] = useState(null);

  const token = localStorage.getItem('token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const requestedBayId = useMemo(() => new URLSearchParams(location.search).get('bayId') || '', [location.search]);
  const isEstimateMode = normalizeDocumentType(currentDocumentType) === 'Estimate';
  const recordLabel = isEstimateMode ? 'Estimate' : 'Job Card';
  const recordPath = isEstimateMode ? '/estimates' : '/jobcards';
  const recordEditPath = (jobId) => `${recordPath}/${jobId}/edit`;
  const recordNumber = createdJob?.jobNumber
    || (isEditMode && id ? String(id).padStart(6, '0') : 'Draft');
  const serviceEngineers = useMemo(() => (
    employees
      .filter((employee) => String(employee.DesignationName || '').trim() === 'Service Engineer')
      .sort((first, second) => String(first.FullName || '').localeCompare(String(second.FullName || '')))
  ), [employees]);
  const assignableEmployees = useMemo(() => (
    [...employees]
      .filter((employee) => Boolean(employee.EmployeeId) && employee.IsActive !== false)
      .sort((first, second) => {
        const firstCode = String(first.EmployeeCode || first.EmployeeId || '');
        const secondCode = String(second.EmployeeCode || second.EmployeeId || '');
        return firstCode.localeCompare(secondCode, undefined, { numeric: true, sensitivity: 'base' })
          || String(first.FullName || '').localeCompare(String(second.FullName || ''));
      })
  ), [employees]);
  const activeTeams = useMemo(() => (
    [...teams]
      .filter((team) => Boolean(team.JobGroupId))
      .sort((first, second) => Number(first.JobGroupId || 0) - Number(second.JobGroupId || 0))
  ), [teams]);
  const selectedServiceEngineer = serviceEngineers.find((employee) => String(employee.EmployeeId) === String(selectedServiceEngineerId));
  const selectedClient = clients.find((client) => String(client.ClientId) === String(selectedClientId));
  const selectedVehicle = vehicles.find((vehicle) => String(vehicle.VehicleInfoId) === String(selectedVehicleId));
  const requestedBay = bays.find((bay) => String(bay.BayId) === String(requestedBayId));
  const selectedBay = bays.find((bay) => String(bay.BayId) === String(selectedBayId));
  const isBayLockedFromBoard = !isEditMode && !isEstimateMode && Boolean(requestedBayId) && Boolean(requestedBay) && !requestedBay.IsOccupied;
  const isReadOnly = jobCardStatus === 'Closed';
  const hasOpenPartIssueRequest = ['Pending', 'Delivered', 'Return Pending'].includes(activePartIssueRequest?.Status || '');
  const jobContextSummary = [
    !isEstimateMode && selectedServiceEngineer?.FullName ? `Service Engineer ${selectedServiceEngineer.FullName}` : null,
    selectedClient?.ClientName ? `Client ${selectedClient.ClientName}` : 'No client selected',
    selectedVehicle ? `Vehicle ${[selectedVehicle.CarNumber, selectedVehicle.Make, selectedVehicle.Model].filter(Boolean).join(' ')}` : 'No vehicle selected',
    `${parts.length} part line${parts.length === 1 ? '' : 's'}`,
    `${labor.length} labor line${labor.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' | ');
  const isPartRowLocked = (part) => isReadOnly || Boolean(part?.submitted) || Boolean(part?.JobCardPartId);
  const canDeletePart = (part) => !isPartRowLocked(part);
  const canDeleteLabor = (item, index) => !isReadOnly && !activeLaborTimers[index] && !item?.startedAt;
  const availableLaborServices = useMemo(() => {
    if (isEstimateMode) {
      return laborServices;
    }

    if (!selectedServiceEngineerId) {
      return [];
    }

    const allowedJobGroupIds = new Set((selectedServiceEngineer?.JobGroupIds || []).map((jobGroupId) => String(jobGroupId)));
    if (!allowedJobGroupIds.size) {
      return [];
    }

    return laborServices.filter((service) => allowedJobGroupIds.has(String(service.jobgroup_id)));
  }, [isEstimateMode, laborServices, selectedServiceEngineer, selectedServiceEngineerId]);
  const filteredPartsInventory = useMemo(() => {
    const search = partSearchTerm.trim().toLowerCase();

    if (!search) {
      return partsInventory;
    }

    return partsInventory
      .filter((part) =>
        [
          part.Part_no,
          part.parts_name,
          part.part_description,
          part.Brand,
          part.vendor_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search))
      );
  }, [partSearchTerm, partsInventory]);
  const filteredLaborServices = useMemo(() => {
    const search = laborSearchTerm.trim().toLowerCase();

    if (!search) {
      return availableLaborServices;
    }

    return availableLaborServices
      .filter((service) =>
        [
          service.description,
          service.jobgroup_name,
          service.price_car_4c_800_1600,
          service.price_mid_1600_2400,
          service.price_large_2500_3200,
          service.price_xlarge_3500_5700,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search))
      );
  }, [availableLaborServices, laborSearchTerm]);

  useEffect(() => {
    if (!isEstimateMode && isServiceEngineerLogin && currentEmployeeId && selectedServiceEngineerId !== currentEmployeeId) {
      setSelectedServiceEngineerId(currentEmployeeId);
    }
  }, [currentEmployeeId, isEstimateMode, isServiceEngineerLogin, selectedServiceEngineerId]);

  const formatEmployeeLookup = useCallback((employee) => {
    if (!employee) {
      return '';
    }

    const code = String(employee.EmployeeCode || employee.EmployeeId || '').trim();
    const name = String(employee.FullName || '').trim();
    return [code, name].filter(Boolean).join(' - ');
  }, []);

  const findEmployeeByLookup = useCallback((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    return assignableEmployees.find((employee) => {
      const code = String(employee.EmployeeCode || '').trim().toLowerCase();
      const id = String(employee.EmployeeId || '').trim().toLowerCase();
      const option = formatEmployeeLookup(employee).toLowerCase();
      return normalized === option || normalized === code || normalized === id;
    }) || null;
  }, [assignableEmployees, formatEmployeeLookup]);

  const resolveEmployeeLookupValue = useCallback((item) => {
    if (String(item?.employeeLookup || '').trim()) {
      return item.employeeLookup;
    }

    const selectedEmployee = assignableEmployees.find((employee) => String(employee.EmployeeId) === String(item?.employeeId || ''));
    return formatEmployeeLookup(selectedEmployee);
  }, [assignableEmployees, formatEmployeeLookup]);

  const formatTeamLookup = useCallback((team) => {
    if (!team) {
      return '';
    }

    const code = String(team.GroupCode || team.teamCode || team.JobGroupId || '').trim();
    const name = String(team.GroupName || '').trim();
    return [code, name].filter(Boolean).join(' - ');
  }, []);

  const findTeamByLookup = useCallback((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    return activeTeams.find((team) => {
      const code = String(team.GroupCode || team.JobGroupId || '').trim().toLowerCase();
      const option = formatTeamLookup(team).toLowerCase();
      const name = String(team.GroupName || '').trim().toLowerCase();
      return normalized === option || normalized === code || normalized === name;
    }) || null;
  }, [activeTeams, formatTeamLookup]);

  const resolveTeamLookupValue = useCallback((item) => {
    if (String(item?.teamLookup || '').trim()) {
      return item.teamLookup;
    }

    const selectedTeam = activeTeams.find((team) => String(team.JobGroupId) === String(item?.teamJobGroupId || ''));
    if (selectedTeam) {
      return formatTeamLookup(selectedTeam);
    }

    if (item?.teamCode && item?.teamName) {
      return `${item.teamCode} - ${item.teamName}`;
    }

    if (item?.teamJobGroupId && item?.teamName) {
      return `${item.teamJobGroupId} - ${item.teamName}`;
    }

    return '';
  }, [activeTeams, formatTeamLookup]);

  const hasTeamAssignment = useCallback((item) => (
    Boolean(
      String(item?.teamLookup || '').trim()
      || String(item?.teamCode || '').trim()
      || String(item?.teamName || '').trim()
      || String(item?.teamJobGroupId || '').trim()
    )
  ), []);

  const loadVehicleOptions = useCallback(async (searchTerm, preservedVehicleId = selectedVehicleId) => {
    setVehicleSearchLoading(true);

    try {
      const response = await axios.get('/api/vehicles', {
        ...auth,
        params: {
          page: 1,
          pageSize: 100,
          search: searchTerm.trim(),
        },
      });

      let nextVehicles = response.data.vehicles || [];

      if (preservedVehicleId && !nextVehicles.some((item) => String(item.VehicleInfoId) === String(preservedVehicleId))) {
        try {
          const detailResponse = await axios.get(`/api/vehicles/${preservedVehicleId}`, auth);
          if (detailResponse.data?.vehicle) {
            nextVehicles = [detailResponse.data.vehicle, ...nextVehicles];
          }
        } catch (detailError) {
          // Keep search results even if the selected vehicle detail cannot be reloaded.
        }
      }

      setVehicles(nextVehicles);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load vehicles.');
    } finally {
      setVehicleSearchLoading(false);
    }
  }, [auth, selectedVehicleId]);

  useEffect(() => {
    setCurrentDocumentType(normalizeDocumentType(documentType));
  }, [documentType]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const fetchAllLaborServices = async () => {
          const firstPage = await axios.get('/api/labor', { ...auth, params: { page: 1, pageSize: 50 } });
          const firstServices = firstPage.data.laborServices || [];
          const totalPages = firstPage.data.pagination?.totalPages || 1;

          if (totalPages <= 1) {
            return firstServices;
          }

          const remainingResponses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              axios.get('/api/labor', { ...auth, params: { page: index + 2, pageSize: 50 } })
            )
          );

          return [
            ...firstServices,
            ...remainingResponses.flatMap((response) => response.data.laborServices || []),
          ];
        };

        const [clientRes, partsRes, laborItems, employeesRes, teamsRes, baysRes] = await Promise.all([
          axios.get('/api/clients', { ...auth, params: { pageSize: 500 } }),
          axios.get('/api/parts', { ...auth, params: { pageSize: 50 } }),
          fetchAllLaborServices(),
          axios.get('/api/time-tracking/employees', auth),
          isEstimateMode ? Promise.resolve({ data: { teams: [] } }) : axios.get('/api/jobcards/teams', auth),
          isEstimateMode ? Promise.resolve({ data: { bays: [] } }) : axios.get('/api/bays', auth),
        ]);

        setClients(clientRes.data.clients || []);
        setPartsInventory(partsRes.data.parts || []);
        setLaborServices(laborItems);
        setEmployees(employeesRes.data.employees || []);
        setTeams(teamsRes.data.teams || []);
        setBays(baysRes.data.bays || []);
      } catch (error) {
        setMessage(error.response?.data?.message || 'Failed to load job card options.');
      }
    };

    loadLookups();
  }, [auth, isEstimateMode]);

  useEffect(() => {
    if (!isEditMode) {
      setAiMessages(getDefaultAiMessages());
      return;
    }

    const loadJobCard = async () => {
      try {
        const response = await axios.get(`/api/jobcards/${id}`, auth);
        const detail = normalizeJobCardDetail(response.data);
        const { jobCard } = detail;

        setSelectedClientId(String(jobCard.ClientId || ''));
        setSelectedVehicleId(String(jobCard.VehicleInfoId || ''));
        setSelectedServiceEngineerId(String(jobCard.ServiceEngineerEmployeeId || ''));
        setOdometerKm(jobCard.OdometerKm || jobCard.OdometerKm === 0 ? String(jobCard.OdometerKm) : '');
        setSelectedBayId(detail.activeBay?.BayId ? String(detail.activeBay.BayId) : '');
        setCreatedJob({ jobCardId: jobCard.JobCardId, jobNumber: jobCard.JobNumber });
        setCurrentDocumentType(normalizeDocumentType(jobCard.DocumentType || documentType));
        setJobCardStatus(jobCard.Status || '');
        setParts(detail.parts);
        setLabor(detail.labor);
        setActiveLaborTimers(detail.activeLaborTimers);
        setActivePartIssueRequest(detail.activePartIssueRequest);
        setAttachments(detail.attachments || []);
        setAiMessages((detail.aiMessages || []).length > 0 ? detail.aiMessages : getDefaultAiMessages());
      } catch (error) {
        setMessage(error.response?.data?.message || 'Failed to load job card for edit.');
      }
    };

    loadJobCard();
  }, [auth, documentType, id, isEditMode]);

  useEffect(() => {
    if (isEditMode || isEstimateMode || !requestedBayId) {
      return;
    }

    if (bays.length === 0) {
      return;
    }

    if (!requestedBay) {
      setMessage('Selected bay from Bay Board was not found.');
      return;
    }

    if (requestedBay.IsOccupied) {
      setMessage(`Bay ${String(requestedBay.BayNumber).padStart(2, '0')} is no longer free. Please choose another bay from Bay Board.`);
      return;
    }

    if (String(selectedBayId) !== String(requestedBay.BayId)) {
      setSelectedBayId(String(requestedBay.BayId));
    }
  }, [bays, isEditMode, isEstimateMode, requestedBayId, requestedBay, selectedBayId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadVehicleOptions(vehicleSearchTerm);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadVehicleOptions, vehicleSearchTerm]);

  useEffect(() => {
    if (!selectedLaborId) {
      return;
    }

    if (!filteredLaborServices.some((service) => String(service.id) === String(selectedLaborId))) {
      setSelectedLaborId('');
    }
  }, [filteredLaborServices, selectedLaborId]);

  useEffect(() => {
    if (!Object.values(activeLaborTimers).some(Boolean)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => setTimerTick(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [activeLaborTimers]);

  const handleVehicleChange = (vehicleInfoId) => {
    setSelectedVehicleId(vehicleInfoId);
    const vehicle = vehicles.find((item) => String(item.VehicleInfoId) === String(vehicleInfoId));
    if (vehicle?.ClientId) {
      setSelectedClientId(String(vehicle.ClientId));
    } else {
      setSelectedClientId('');
    }
  };

  const addInventoryPart = () => {
    if (isReadOnly) {
      return;
    }

    const part = partsInventory.find((item) => String(item.PartId) === String(selectedPartId));
    if (!part) {
      setMessage('Please select an inventory part first.');
      return;
    }

    setParts((current) => [
      ...current,
      {
        partId: part.PartId,
        partNumber: part.Part_no,
        description: part.parts_name || part.part_description || part.Part_no,
        cost: isEstimateMode ? 0 : toMoneyNumber(part.unit_price ?? part.opening_value),
        price: isEstimateMode ? toMoneyNumber(part.Sale_Price) : 0,
        quantity: 1,
        unit: part.unit || 'pcs',
        taxable: false,
      },
    ]);
    setSelectedPartId('');
    setPartSearchTerm('');
    setMessage('');
  };

  const addBlankPart = () => {
    if (isReadOnly) {
      return;
    }

    setParts((current) => [
      ...current,
      {
        partId: null,
        partNumber: '',
        description: '',
        cost: 0,
        price: 0,
        quantity: 1,
        unit: 'pcs',
        taxable: false,
      },
    ]);
    setMessage('');
  };

  const addLaborService = () => {
    if (isReadOnly) {
      return;
    }

    if (!isEstimateMode && !selectedServiceEngineerId) {
      setMessage('Select service engineer first.');
      return;
    }

    const service = filteredLaborServices.find((item) => String(item.id) === String(selectedLaborId));
    if (!service) {
      setMessage('Please select a labor service first.');
      return;
    }

    const pricing = getLaborPricingForVehicle(service, selectedVehicle);
    const rate = toMoneyNumber(pricing.price);
    setLabor((current) => [
      ...current,
      {
        laborServiceId: service.id,
        name: service.description,
        description: service.jobgroup_name,
        laborType: 'Service',
        rate,
        hours: Number(pricing.durationMinutes || 60) / 60,
        fixedRate: false,
        taxable: false,
        employeeId: '',
        employeeLookup: '',
        teamJobGroupId: '',
        teamCode: '',
        teamName: '',
        teamLookup: '',
      },
    ]);
    setSelectedLaborId('');
    setLaborSearchTerm('');
    setMessage('');
  };

  const updatePart = (index, field, value) => {
    if (isReadOnly) {
      return;
    }

    setParts((current) => current.map((part, itemIndex) => {
      if (itemIndex !== index || isPartRowLocked(part)) {
        return part;
      }

      return { ...part, [field]: value };
    }));
  };

  const updateLabor = (index, field, value) => {
    if (isReadOnly) {
      return;
    }

    setLabor((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)));
  };

  const handleLaborEmployeeLookupChange = (index, value) => {
    if (isReadOnly) {
      return;
    }

    const matchedEmployee = findEmployeeByLookup(value);
    setLabor((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }

      if (!String(value || '').trim()) {
        return {
          ...item,
          employeeId: '',
          employeeLookup: '',
        };
      }

      if (matchedEmployee) {
        return {
          ...item,
          employeeId: String(matchedEmployee.EmployeeId),
          employeeLookup: formatEmployeeLookup(matchedEmployee),
          teamJobGroupId: '',
          teamCode: '',
          teamName: '',
          teamLookup: '',
        };
      }

      return {
        ...item,
        employeeId: '',
        employeeLookup: value,
        teamJobGroupId: '',
        teamCode: '',
        teamName: '',
        teamLookup: '',
      };
    }));
  };

  const handleLaborTeamLookupChange = (index, value) => {
    if (isReadOnly) {
      return;
    }

    const matchedTeam = findTeamByLookup(value);
    setLabor((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }

      if (!String(value || '').trim()) {
        return {
          ...item,
          teamJobGroupId: '',
          teamCode: '',
          teamName: '',
          teamLookup: '',
        };
      }

      if (matchedTeam) {
        return {
          ...item,
          employeeId: '',
          employeeLookup: '',
          teamJobGroupId: String(matchedTeam.JobGroupId),
          teamCode: matchedTeam.GroupCode || '',
          teamName: matchedTeam.GroupName || '',
          teamLookup: formatTeamLookup(matchedTeam),
        };
      }

      return {
        ...item,
        employeeId: '',
        employeeLookup: '',
        teamJobGroupId: '',
        teamCode: '',
        teamName: '',
        teamLookup: value,
      };
    }));
  };

  const refreshJobCardDetail = async (jobCardId) => {
    const detailResponse = await axios.get(`/api/jobcards/${jobCardId}?t=${Date.now()}`, auth);
    const detail = normalizeJobCardDetail(detailResponse.data);
    setCurrentDocumentType(normalizeDocumentType(detail.jobCard.DocumentType || documentType));
    setSelectedServiceEngineerId(String(detail.jobCard.ServiceEngineerEmployeeId || ''));
    setParts(detail.parts);
    setLabor(detail.labor);
    setActiveLaborTimers(detail.activeLaborTimers);
    setActivePartIssueRequest(detail.activePartIssueRequest);
    setAttachments(detail.attachments || []);
    setAiMessages((detail.aiMessages || []).length > 0 ? detail.aiMessages : getDefaultAiMessages());
    setJobCardStatus(detail.jobCard.Status || '');
    setSelectedBayId(detail.activeBay?.BayId ? String(detail.activeBay.BayId) : selectedBayId);
    return detail;
  };

  const removeUnsavedLaborAt = (index) => {
    setLabor((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setActiveLaborTimers((current) => Object.entries(current).reduce((nextTimers, [key, value]) => {
      const timerIndex = Number(key);
      if (timerIndex < index) {
        nextTimers[timerIndex] = value;
      } else if (timerIndex > index) {
        nextTimers[timerIndex - 1] = value;
      }
      return nextTimers;
    }, {}));
  };

  const deletePart = async (index) => {
    const part = parts[index];

    if (!canDeletePart(part)) {
      setMessage('Part cannot be deleted after submit.');
      return;
    }

    if (!part?.JobCardPartId) {
      setParts((current) => current.filter((_, itemIndex) => itemIndex !== index));
      setMessage('');
      return;
    }

    const jobCardId = createdJob?.jobCardId || id;
    if (!jobCardId) {
      setParts((current) => current.filter((_, itemIndex) => itemIndex !== index));
      setMessage('');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await axios.delete(`/api/jobcards/${jobCardId}/parts/${part.JobCardPartId}`, auth);
      await refreshJobCardDetail(jobCardId);
      setMessage('Part deleted.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to delete part.');
    } finally {
      setSaving(false);
    }
  };

  const rejectPart = (index) => {
    const part = parts[index];

    if (!part?.JobCardPartId) {
      setMessage('Only issued parts can be rejected via this action.');
      return;
    }

    if (activePartIssueRequest?.Status === 'Return Pending') {
      setMessage('Inventory return acceptance is already pending for this job card.');
      return;
    }

    setRejectConfirmIndex(index);
  };

  const confirmReject = async () => {
    if (rejectConfirmIndex === null) {
      return;
    }

    const part = parts[rejectConfirmIndex];
    const jobCardId = createdJob?.jobCardId || id;
    if (!jobCardId) {
      setMessage('Job card ID not available.');
      setRejectConfirmIndex(null);
      return;
    }

    setSaving(true);
    setMessage('');
    setRejectConfirmIndex(null);

    try {
      console.log('Rejecting part:', part.JobCardPartId, 'from job card:', jobCardId);
      const deleteResponse = await axios.delete(`/api/jobcards/${jobCardId}/parts/${part.JobCardPartId}`, auth);
      console.log('Delete response:', deleteResponse.data);
      
      if (!deleteResponse.data?.success) {
        setMessage('Part deletion returned unsuccessful status.');
        setSaving(false);
        return;
      }

      console.log('Refreshing job card detail...');
      const detail = await refreshJobCardDetail(jobCardId);
      console.log('Refreshed detail parts count:', detail.parts.length);
      
      setMessage(deleteResponse.data?.message || 'Part reject request sent to inventory activity for acceptance.');
    } catch (error) {
      console.error('Reject part error:', error);
      setMessage(error.response?.data?.message || error.message || 'Failed to reject part.');
    } finally {
      setSaving(false);
    }
  };

  const cancelReject = () => {
    setRejectConfirmIndex(null);
  };

  const deleteLabor = async (index) => {
    const item = labor[index];

    if (!canDeleteLabor(item, index)) {
      setMessage('Labor cannot be deleted after start.');
      return;
    }

    if (!item?.JobCardLaborId) {
      removeUnsavedLaborAt(index);
      setMessage('');
      return;
    }

    const jobCardId = createdJob?.jobCardId || id;
    if (!jobCardId) {
      removeUnsavedLaborAt(index);
      setMessage('');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await axios.delete(`/api/jobcards/${jobCardId}/labor/${item.JobCardLaborId}`, auth);
      await refreshJobCardDetail(jobCardId);
      setMessage('Labor deleted.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to delete labor.');
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentSelection = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    const allowedFiles = files.filter((file) => (
      String(file.type || '').startsWith('image/') || String(file.type || '') === 'application/pdf'
    ));

    if (!allowedFiles.length) {
      setMessage('Only image and PDF files are allowed.');
      event.target.value = '';
      return;
    }

    setPendingAttachments((current) => [...current, ...allowedFiles]);
    setMessage('');
    event.target.value = '';
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const uploadPendingAttachments = async (jobCardId) => {
    if (!jobCardId || !pendingAttachments.length) {
      return;
    }

    const formData = new FormData();
    pendingAttachments.forEach((file) => {
      formData.append('files', file);
    });

    const response = await axios.post(`/api/jobcards/${jobCardId}/attachments`, formData, {
      headers: {
        ...auth.headers,
        'Content-Type': 'multipart/form-data',
      },
    });

    setAttachments((current) => [...(response.data.attachments || []), ...current]);
    setPendingAttachments([]);
  };

  const deleteAttachment = async (attachmentId) => {
    const jobCardId = createdJob?.jobCardId || id;
    if (!jobCardId || !attachmentId) {
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await axios.delete(`/api/jobcards/${jobCardId}/attachments/${attachmentId}`, auth);
      setAttachments((current) => current.filter((attachment) => Number(attachment.AttachmentId) !== Number(attachmentId)));
      setMessage('Attachment deleted.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to delete attachment.');
    } finally {
      setSaving(false);
    }
  };

  const applyJobCard = async (silent = false) => {
    if (isReadOnly) {
      if (!silent) setMessage(`Closed ${recordLabel.toLowerCase()}s are view only.`);
      return false;
    }

    if (!isEstimateMode && !selectedServiceEngineerId) {
      if (!silent) setMessage('Select service engineer first.');
      return false;
    }

    if (!selectedVehicleId) {
      if (!silent) setMessage('Select car number first.');
      return false;
    }

    if (!isEstimateMode && !String(odometerKm || '').trim()) {
      if (!silent) setMessage('Enter KM first.');
      return false;
    }

    if (!isEstimateMode && !selectedBayId) {
      if (!silent) setMessage('Select bay first.');
      return false;
    }

    if (!silent) {
      setSaving(true);
      setMessage('');
    }

    try {
      const payload = {
        clientId: selectedClientId || selectedVehicle?.ClientId || '',
        vehicleInfoId: selectedVehicleId,
        odometerKm,
        bayId: isEstimateMode ? null : selectedBayId,
        serviceEngineerEmployeeId: isEstimateMode ? null : selectedServiceEngineerId,
        documentType: currentDocumentType,
        parts: parts.filter((part) =>
          Boolean(part.partId)
          || Boolean(String(part.partNumber || '').trim())
          || Boolean(String(part.description || '').trim())
          || toMoneyNumber(part.price) > 0
        ),
        labor,
      };

      const response = await axios[isEditMode ? 'put' : 'post'](
        isEditMode ? `/api/jobcards/${id}` : '/api/jobcards',
        payload,
        auth
      );

      const effectiveJobCardId = response.data.jobCardId || createdJob?.jobCardId || id;
      const jobNumber = response.data.jobNumber || createdJob?.jobNumber;
      setCurrentDocumentType(normalizeDocumentType(response.data.documentType || currentDocumentType));
      if (response.data.bay?.BayId) {
        setSelectedBayId(String(response.data.bay.BayId));
      }
      if (pendingAttachments.length) {
        await uploadPendingAttachments(effectiveJobCardId);
      }
      if (!isEditMode) {
        setCreatedJob(response.data);
        if (!silent) navigate(recordEditPath(response.data.jobCardId));
        return true;
      }
      const detail = await refreshJobCardDetail(effectiveJobCardId);
      if (!silent) {
        setMessage(`${recordLabel} #${jobNumber} updated.`);
      }
      return detail;
    } catch (error) {
      if (!silent) {
        setMessage(error.response?.data?.message || (isEditMode ? `Failed to update ${recordLabel.toLowerCase()}.` : `Failed to create ${recordLabel.toLowerCase()}.`));
      }
      return false;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const submitPartIssueRequest = async () => {
    if (isEstimateMode) {
      setMessage('Part issue request is not available for estimates.');
      return;
    }

    if (isReadOnly) {
      return;
    }

    if (!selectedServiceEngineerId) {
      setMessage('Select service engineer first.');
      return;
    }

    if (!selectedVehicleId) {
      setMessage('Select car number first.');
      return;
    }

    if (!String(odometerKm || '').trim()) {
      setMessage('Enter KM first.');
      return;
    }

    if (!selectedBayId) {
      setMessage('Select bay first.');
      return;
    }

    const requestItems = parts
      .filter((row) => !row.JobCardPartId && !row.submitted)
      .map((row) => ({
        partId: row.partId ? Number(row.partId) : null,
        partNumber: String(row.partNumber || '').trim(),
        description: row.description || null,
        quantity: toMoneyNumber(row.quantity),
        unit: row.unit || null,
      }))
      .filter((row) => row.partNumber && row.quantity > 0);

    if (requestItems.length === 0) {
      setMessage('Enter at least one part number and quantity for store issue.');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      let jobCardId = createdJob?.jobCardId || id;
      let jobNumber = createdJob?.jobNumber;

      if (!jobCardId) {
        const createResponse = await axios.post('/api/jobcards', {
          clientId: selectedClientId || selectedVehicle?.ClientId || '',
          vehicleInfoId: selectedVehicleId,
          odometerKm,
          bayId: isEstimateMode ? null : selectedBayId,
          serviceEngineerEmployeeId: selectedServiceEngineerId,
          documentType: currentDocumentType,
          parts: [],
          labor,
        }, auth);
        jobCardId = createResponse.data.jobCardId;
        jobNumber = createResponse.data.jobNumber;
        setCreatedJob(createResponse.data);
        if (createResponse.data.bay?.BayId) {
          setSelectedBayId(String(createResponse.data.bay.BayId));
        }
      }

      const response = await axios.post(`/api/jobcards/${jobCardId}/part-issue-request`, { items: requestItems }, auth);
      setParts((current) => current.map((row) => (
        row.JobCardPartId || row.submitted
          ? row
          : { ...row, submitted: true }
      )));
      setActivePartIssueRequest({
        PartIssueRequestId: response.data.partIssueRequestId,
        Status: response.data.status,
      });
      setJobCardStatus('Waiting Parts');
      setMessage(`Part issue request sent to store for Job Card #${jobNumber || jobCardId}.`);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to request part issue.');
    } finally {
      setSaving(false);
    }
  };

  const receivePartIssueRequest = async () => {
    const jobCardId = createdJob?.jobCardId || id;
    const requestId = activePartIssueRequest?.PartIssueRequestId;

    if (!jobCardId || !requestId) {
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await axios.post(`/api/jobcards/${jobCardId}/part-issue-request/${requestId}/receive`, {}, auth);
      const response = await axios.get(`/api/jobcards/${jobCardId}`, auth);
      setParts(
        (response.data.parts || []).map((part) => ({
          ...part,
          taxable: Boolean(part.taxable),
        }))
      );
      setActivePartIssueRequest(response.data.activePartIssueRequest || null);
      setJobCardStatus('WIP');
      setMessage('Issued parts received and added to this job card.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to receive issued parts.');
    } finally {
      setSaving(false);
    }
  };

  const submitEstimateInventorySelection = async () => {
    if (!isEstimateMode) {
      return;
    }

    const hasEstimateItems = parts.some((part) => (
      Boolean(part.partId)
      || Boolean(String(part.partNumber || '').trim())
      || Boolean(String(part.description || '').trim())
      || toMoneyNumber(part.price) > 0
    ));

    if (!hasEstimateItems) {
      setMessage('Select at least one inventory part first.');
      return;
    }

    await applyJobCard();
  };

  const closeJobCard = async () => {
    if (isReadOnly) {
      setMessage(`Closed ${recordLabel.toLowerCase()}s are view only.`);
      return;
    }

    if (!createdJob?.jobCardId) {
      setMessage(`Create the ${recordLabel.toLowerCase()} first.`);
      return;
    }

    const hasActiveTimers = Object.values(activeLaborTimers).some(Boolean);
    if (!isEstimateMode && hasActiveTimers) {
      setMessage('Please finish all active timers before closing the job card.');
      return;
    }

    const confirmed = window.confirm(
      isEstimateMode
        ? `Close Estimate #${createdJob.jobNumber}? It will appear in Inv & Est as an estimate.`
        : `Close Job Card #${createdJob.jobNumber}? This will prepare the final bill.`
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      await axios.post(`/api/jobcards/${createdJob.jobCardId}/close`, {}, auth);
      setMessage(
        isEstimateMode
          ? `Estimate #${createdJob.jobNumber} closed and added to Inv & Est.`
          : `Job Card #${createdJob.jobNumber} closed. Final bill is ready.`
      );
      navigate(recordPath);
    } catch (error) {
      setMessage(error.response?.data?.message || `Failed to close ${recordLabel.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const hasActiveLaborTimers = Object.values(activeLaborTimers).some(Boolean);

  const startWork = async (rowIndex) => {
    if (isReadOnly) {
      setMessage(`Closed ${recordLabel.toLowerCase()}s are view only.`);
      return;
    }

    if (!selectedServiceEngineerId) {
      setMessage('Select service engineer first.');
      return;
    }

    if (!createdJob?.jobCardId) {
      setMessage(`Create the ${recordLabel.toLowerCase()} first.`);
      return;
    }

    try {
      const detail = await applyJobCard(true);
      if (!detail) return;

      const savedLaborRow = detail.labor[rowIndex];
      const laborId = savedLaborRow?.JobCardLaborId;
      const employeeId = savedLaborRow?.employeeId ? Number(savedLaborRow.employeeId) : null;
      const hasTeam = hasTeamAssignment(savedLaborRow);

      if (!employeeId && !hasTeam) {
        setMessage('Select employee code or team code first.');
        return;
      }

      await axios.post(`/api/jobcards/${createdJob.jobCardId}/start`, { employeeId, laborId }, auth);
      await refreshJobCardDetail(createdJob.jobCardId);
      setTimerTick(Date.now());
      setMessage(
        hasTeam
          ? `Team timer started for Job Card #${createdJob.jobNumber}.`
          : `Work timer started for Job Card #${createdJob.jobNumber}.`
      );
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to start work timer.');
    }
  };

  const finishWork = async (rowIndex, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!activeLaborTimers[rowIndex]) {
      setMessage('No running timer to finish for this row.');
      return;
    }

    if (!createdJob?.jobCardId) {
      setMessage(`Create the ${recordLabel.toLowerCase()} first.`);
      return;
    }

    try {
      const detail = await applyJobCard(true);
      if (!detail) return;

      const savedLaborRow = detail.labor[rowIndex];
      const laborId = savedLaborRow?.JobCardLaborId;
      const employeeId = savedLaborRow?.employeeId ? Number(savedLaborRow.employeeId) : null;
      const hasTeam = hasTeamAssignment(savedLaborRow);

      if (!employeeId && !hasTeam) {
        setMessage('Select employee code or team code first.');
        return;
      }

      const response = await axios.post(`/api/jobcards/${createdJob.jobCardId}/finish`, {
        employeeId,
        laborId,
      }, auth);
      const elapsedMinutes = Number(response.data.elapsedMinutes || 0);
      await refreshJobCardDetail(createdJob.jobCardId);
      setMessage(
        `${hasTeam ? 'Team work finished' : 'Work finished'}. Added ${Math.round(elapsedMinutes)} minutes. Total recorded time: ${Math.round(response.data.totalMinutes || 0)} minutes.`
      );
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to finish work timer.');
    }
  };

  const totalParts = parts.reduce((sum, part) => sum + toMoneyNumber(part.price) * toMoneyNumber(part.quantity), 0);
  const totalLabor = labor.reduce(
    (sum, item) => sum + (item.fixedRate ? toMoneyNumber(item.rate) : toMoneyNumber(item.rate) * toMoneyNumber(item.hours)),
    0
  );

  const openAiChat = () => {
    setIsAiChatOpen(true);
  };

  const closeAiChat = () => {
    setIsAiChatOpen(false);
  };

  const submitAiPrompt = async () => {
    const prompt = aiDraft.trim();
    if (!prompt || aiSending) {
      return;
    }

    const conversationHistory = aiMessages.slice(-8);
    const draftContext = {
      recordLabel,
      recordNumber,
      status: jobCardStatus || (isEditMode ? 'Open' : 'Draft'),
      odometerKm,
      client: {
        name: selectedClient?.ClientName || '',
        phone: selectedClient?.Phone || '',
      },
      vehicle: {
        carNumber: selectedVehicle?.CarNumber || '',
        vin: selectedVehicle?.VINNumber || '',
        make: selectedVehicle?.Make || '',
        model: selectedVehicle?.Model || '',
        year: selectedVehicle?.Year || '',
        cc: selectedVehicle?.CC || '',
      },
      bay: {
        label: selectedBay?.BayNumber ? `Bay ${String(selectedBay.BayNumber).padStart(2, '0')}${selectedBay.BayName ? ` - ${selectedBay.BayName}` : ''}` : '',
      },
      parts: parts.map((part) => ({
        partNumber: part.partNumber || '',
        description: part.description || '',
        quantity: part.quantity || 0,
        unit: part.unit || '',
        price: part.price || 0,
      })),
      labor: labor.map((item) => ({
        name: item.name || '',
        description: item.description || '',
        employeeName: employees.find((employee) => String(employee.EmployeeId) === String(item.employeeId))?.FullName || '',
        hours: item.hours || 0,
        rate: item.rate || 0,
      })),
    };

    setAiMessages((current) => ([
      ...current,
      { role: 'user', text: prompt },
    ]));
    setAiDraft('');
    setAiSending(true);

    try {
      const response = await axios.post('/api/jobcards/ai-assistant', {
        jobCardId: createdJob?.jobCardId || null,
        message: prompt,
        messages: conversationHistory,
        draftContext,
      }, auth);

      setAiMessages((current) => ([
        ...current,
        {
          role: 'assistant',
          text: response.data?.reply || 'No AI reply received.',
        },
      ]));
    } catch (error) {
      setAiMessages((current) => ([
        ...current,
        {
          role: 'assistant',
          text: error.response?.data?.message || 'AI assistant is not available right now.',
        },
      ]));
    } finally {
      setAiSending(false);
    }
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="new-jobcard-page">
        <header className="new-jobcard-header">
          <button className="app-back-button" onClick={() => navigate(recordPath)} aria-label={`Back to ${recordLabel}s`}>
            Back
          </button>
          <h1>
            {isReadOnly ? `View ${recordLabel}` : isEditMode ? `Edit ${recordLabel}` : `New ${recordLabel}`} <a href="#job-number">#{recordNumber}</a>
          </h1>
          {isReadOnly && <span className="jobcard-readonly-badge">{jobCardStatus}</span>}
          <span className="jobcard-info">i</span>
        </header>

        {isReadOnly && (
          <div className="jobcard-readonly-notice">
            This {recordLabel.toLowerCase()} is closed and can only be viewed.
          </div>
        )}

        <section className="new-jobcard-panel">
          {!isEstimateMode && (
            <div className="jobcard-selector-card service-engineer-card">
              <span className="selector-icon">SE</span>
              <label>
                Service Engineer
                <select
                  value={selectedServiceEngineerId}
                  onChange={(event) => {
                    setSelectedServiceEngineerId(event.target.value);
                    setSelectedLaborId('');
                    setLaborSearchTerm('');
                    setMessage('');
                  }}
                  disabled={isReadOnly || isServiceEngineerLogin}
                >
                  <option value="">{isServiceEngineerLogin ? 'Your service engineer account' : 'Select service engineer'}</option>
                  {serviceEngineers.map((employee) => (
                    <option key={employee.EmployeeId} value={employee.EmployeeId}>
                      {employee.FullName}
                    </option>
                  ))}
                </select>
                <small className="vehicle-search-status">
                  {selectedServiceEngineer
                    ? (selectedServiceEngineer.JobGroupName
                      ? `Job groups: ${selectedServiceEngineer.JobGroupName}`
                      : 'No job group assigned for this service engineer.')
                    : 'Select service engineer first to load job list.'}
                </small>
              </label>
            </div>
          )}

          <div className="jobcard-selector-card client-card">
            <span className="selector-icon">●</span>
            <label>
              Client
              <select value={selectedClientId} onChange={(event) => setSelectedClientId(event.target.value)} disabled={isReadOnly || Boolean(selectedVehicle?.ClientId)}>
                <option value="">{selectedVehicleId ? 'No linked client found' : 'Select car number first'}</option>
                {clients.map((client) => (
                  <option key={client.ClientId} value={client.ClientId}>
                    {client.ClientName} {client.Phone ? `- ${client.Phone}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="jobcard-selector-card vehicle-card">
            <span className="selector-icon">▣</span>
            <label>
              Car Number
              <input
                type="search"
                value={vehicleSearchTerm}
                onChange={(event) => setVehicleSearchTerm(event.target.value)}
                placeholder="Search car number, VIN, make, model"
                disabled={isReadOnly}
              />
              <select value={selectedVehicleId} onChange={(event) => handleVehicleChange(event.target.value)} disabled={isReadOnly}>
                <option value="">{vehicleSearchLoading ? 'Searching vehicles...' : 'Select car number'}</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.VehicleInfoId} value={vehicle.VehicleInfoId}>
                    {[vehicle.CarNumber, vehicle.Year, vehicle.Make, vehicle.Model].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
              <small className="vehicle-search-status">
                {vehicleSearchLoading
                  ? 'Searching vehicle list...'
                  : `${vehicles.length} vehicle match${vehicles.length === 1 ? '' : 'es'} shown`}
              </small>
            </label>
          </div>

          <div className="jobcard-selector-card km-card">
            <span className="selector-icon">KM</span>
            <label>
              KM
              <input
                value={odometerKm}
                onChange={(event) => setOdometerKm(event.target.value)}
                placeholder="Enter current KM"
                inputMode="numeric"
                disabled={isReadOnly}
              />
            </label>
          </div>

          {!isEstimateMode && (
            <div className="jobcard-selector-card bay-card">
              <span className={`selector-icon ${selectedBay?.IsOccupied ? 'bay-busy-icon' : 'bay-free-icon'}`}>B</span>
              <label>
                Bay
                <select value={selectedBayId} onChange={(event) => setSelectedBayId(event.target.value)} disabled={isReadOnly || isBayLockedFromBoard}>
                  <option value="">{bays.some((bay) => !bay.IsOccupied) ? 'Select bay' : 'No bay available'}</option>
                  {bays
                    .filter((bay) => !bay.IsOccupied || String(bay.BayId) === String(selectedBayId))
                    .map((bay) => (
                      <option key={bay.BayId} value={bay.BayId}>
                        Bay {String(bay.BayNumber).padStart(2, '0')}{bay.IsOccupied ? ' - Current' : ' - Free'}
                      </option>
                    ))}
                </select>
                {isBayLockedFromBoard && selectedBay && (
                  <small className="bay-lock-status">
                    Fixed from Bay Board: Bay {String(selectedBay.BayNumber).padStart(2, '0')}
                  </small>
                )}
              </label>
            </div>
          )}

          <div className="jobcard-selector-card einstein-card">
            <span className="selector-icon">✓</span>
            <div className="jobcard-selector-content">
              <strong>Albert Einstein</strong>
              <small>Open workshop AI chat</small>
            </div>
            <button type="button" className="jobcard-ai-trigger" onClick={openAiChat}>
              Open Chat
            </button>
          </div>

          <div className="jobcard-selector-card integrations-card integrations-card-hidden">
            <span className="selector-icon">✣</span>
            <label>
              Integrations
              <select defaultValue="" disabled={isReadOnly}>
                <option value="">AI Diagnose, M1 Parts, ProDemand</option>
              </select>
            </label>
          </div>
        </section>

        <section className="jobcard-selected-strip">
          {!isEstimateMode && (
            <span>{selectedServiceEngineer ? `Service Engineer: ${selectedServiceEngineer.FullName}` : 'No service engineer selected'}</span>
          )}
          <span>{selectedClient ? `Client: ${selectedClient.ClientName}` : 'No client selected'}</span>
          <span>
            {selectedVehicle
              ? `Vehicle: ${[selectedVehicle.CarNumber, selectedVehicle.Make, selectedVehicle.Model].filter(Boolean).join(' ')}`
              : 'No vehicle selected'}
          </span>
        </section>

        <section className="parts-labor-section">
          <div className="parts-labor-heading">
            <h2>{isEstimateMode ? 'Estimate Items' : 'Parts and Labor'}</h2>
                      </div>

          <div className="parts-labor-card">
            <h3>Parts</h3>
            <table className="jobcard-entry-table">
           <thead>
                 <tr>
                   <th>S.No.</th>
                   <th>Part No</th>
                   <th>Description</th>
                   <th>Price</th>
                   <th>Qty</th>
                   <th>Unit</th>
                   <th>Taxable</th>
                   <th>Amount</th>
                   <th>Action</th>
                 </tr>
               </thead>
              <tbody>
                 {parts.length === 0 ? (
                   <tr>
                     <td colSpan="9" className="entry-empty">{money(0)}</td>
                   </tr>
                 ) : (
                   parts.map((part, index) => (
                     <tr key={part.JobCardPartId || part.PartIssueRequestItemId || `${part.partId || 'row'}-${index}`}>
                       <td>{index + 1}</td>
                       <td><input value={part.partNumber || ''} onChange={(event) => updatePart(index, 'partNumber', event.target.value)} disabled={isPartRowLocked(part)} /></td>
                       <td><input value={part.description || ''} onChange={(event) => updatePart(index, 'description', event.target.value)} disabled={isPartRowLocked(part)} /></td>
                       <td>
                         <input
                           value={isEstimateMode || part.JobCardPartId ? (part.price || 0) : ''}
                           onChange={(event) => updatePart(index, 'price', event.target.value)}
                           disabled={isPartRowLocked(part) || (!isEstimateMode && !part.JobCardPartId)}
                           placeholder={!isEstimateMode && !part.JobCardPartId ? 'After issue' : ''}
                         />
                       </td>
                       <td><input value={part.quantity} onChange={(event) => updatePart(index, 'quantity', event.target.value)} disabled={isPartRowLocked(part)} /></td>
                       <td><input value={part.unit || ''} onChange={(event) => updatePart(index, 'unit', event.target.value)} disabled={isPartRowLocked(part)} /></td>
                       <td><input type="checkbox" checked={part.taxable} onChange={(event) => updatePart(index, 'taxable', event.target.checked)} disabled={isPartRowLocked(part)} /></td>
                       <td>{money(toMoneyNumber(part.price) * toMoneyNumber(part.quantity))}</td>
                       <td>
                         <div style={{ display: 'flex', gap: '6px' }}>
                           <button
                             type="button"
                             className="jobcard-delete-row-button"
                             onClick={() => deletePart(index)}
                             disabled={saving || !canDeletePart(part)}
                           >
                             Delete
                           </button>
                           {part?.JobCardPartId && !isReadOnly && (
                             <button
                               type="button"
                               className="jobcard-reject-row-button"
                               onClick={() => rejectPart(index)}
                               disabled={saving || activePartIssueRequest?.Status === 'Return Pending'}
                             >
                               Reject
                             </button>
                           )}
                         </div>
                       </td>
                     </tr>
                   ))
                 )}
              </tbody>
            </table>
            <div className="jobcard-row-actions">
              <div className="inventory-search-picker">
                <input
                  type="search"
                  value={partSearchTerm}
                  onChange={(event) => {
                    setPartSearchTerm(event.target.value);
                    setSelectedPartId('');
                  }}
                  placeholder={isEstimateMode ? 'Search inventory part number, name, brand' : 'Search inventory by part no, name, brand'}
                  disabled={isReadOnly}
                />
                <select value={selectedPartId} onChange={(event) => setSelectedPartId(event.target.value)} disabled={isReadOnly}>
                  <option value="">{isEstimateMode ? 'Select part number from Inventory' : 'Select from Inventory'}</option>
                  {filteredPartsInventory.map((part) => (
                    <option key={part.PartId} value={part.PartId}>
                      {isEstimateMode
                        ? `${part.Part_no || part.parts_name} - Tk ${toMoneyNumber(part.Sale_Price).toFixed(2)}`
                        : `${part.Part_no || part.parts_name} - ${part.parts_name}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="jobcard-action-buttons">
                <button type="button" onClick={addInventoryPart} disabled={isReadOnly}>Add selected</button>
                <button type="button" onClick={addBlankPart} disabled={isReadOnly}>New part +</button>
                {isEstimateMode && (
                  <button type="button" onClick={submitEstimateInventorySelection} disabled={saving || isReadOnly}>
                    Submit
                  </button>
                )}
                {!isEstimateMode && !hasOpenPartIssueRequest && (
                  <button type="button" onClick={submitPartIssueRequest} disabled={saving || isReadOnly}>
                    Submit
                  </button>
                )}
                {!isEstimateMode && activePartIssueRequest?.Status === 'Pending' && (
                  <button type="button" className="store-wait-button" disabled>
                    Wait for Receive
                  </button>
                )}
                {!isEstimateMode && activePartIssueRequest?.Status === 'Delivered' && (
                  <button type="button" className="store-receive-button" onClick={receivePartIssueRequest} disabled={saving}>
                    Receive Goods
                  </button>
                )}
                {!isEstimateMode && activePartIssueRequest?.Status === 'Return Pending' && (
                  <button type="button" className="store-wait-button" disabled>
                    Wait for Inventory Accept
                  </button>
                )}
              </div>
            </div>

            <h3>Labor</h3>
            <table className="jobcard-entry-table">
           <thead>
                 <tr>
                   <th>S.No.</th>
                   <th>Name</th>
                   <th>Description</th>
                   {!isEstimateMode && <th>Employee Code</th>}
                   {!isEstimateMode && <th>Team Code</th>}
                   <th>Rate</th>
                   <th>Hours</th>
                   <th>Fixed Rate</th>
                   <th>Taxable</th>
                   <th>Amount</th>
                   {!isEstimateMode && <th>Timer</th>}
                   <th>Action</th>
                 </tr>
               </thead>
              <tbody>
                 {labor.length === 0 ? (
                   <tr>
                     <td colSpan={isEstimateMode ? 9 : 12} className="entry-empty">0.00 hrs. {money(0)}</td>
                   </tr>
                 ) : (
                   labor.map((item, index) => (
                     <tr key={item.JobCardLaborId || item.rowId || `${item.laborServiceId || 'new'}-${index}`}>
                       <td>{index + 1}</td>
                       <td><input value={item.name || ''} onChange={(event) => updateLabor(index, 'name', event.target.value)} disabled={isReadOnly} /></td>
                       <td><input value={item.description || ''} onChange={(event) => updateLabor(index, 'description', event.target.value)} disabled={isReadOnly} /></td>
                       {!isEstimateMode && (
                         <td>
                           <input
                             list="jobcard-employee-options"
                             value={resolveEmployeeLookupValue(item)}
                             onChange={(event) => handleLaborEmployeeLookupChange(index, event.target.value)}
                             placeholder="Write employee code"
                             disabled={isReadOnly || Boolean(activeLaborTimers[index]) || (!String(resolveEmployeeLookupValue(item) || '').trim() && Boolean(String(resolveTeamLookupValue(item) || '').trim()))}
                           />
                         </td>
                       )}
                       {!isEstimateMode && (
                         <td>
                           <input
                             list="jobcard-team-options"
                             value={resolveTeamLookupValue(item)}
                             onChange={(event) => handleLaborTeamLookupChange(index, event.target.value)}
                             placeholder="Write team code"
                             disabled={isReadOnly || Boolean(activeLaborTimers[index]) || (!String(resolveTeamLookupValue(item) || '').trim() && Boolean(String(resolveEmployeeLookupValue(item) || '').trim()))}
                           />
                         </td>
                       )}
                       <td><input value={item.rate} onChange={(event) => updateLabor(index, 'rate', event.target.value)} disabled={isReadOnly} /></td>
                       <td><input value={item.hours} onChange={(event) => updateLabor(index, 'hours', event.target.value)} disabled={isReadOnly} /></td>
                       <td><input type="checkbox" checked={item.fixedRate} onChange={(event) => updateLabor(index, 'fixedRate', event.target.checked)} disabled={isReadOnly} /></td>
                       <td><input type="checkbox" checked={item.taxable} onChange={(event) => updateLabor(index, 'taxable', event.target.checked)} disabled={isReadOnly} /></td>
                       <td>{money(item.fixedRate ? item.rate : toMoneyNumber(item.rate) * toMoneyNumber(item.hours))}</td>
                       {!isEstimateMode && (
                         <td>
                           <div className="labor-timer-actions">
                             {activeLaborTimers[index] ? (
                               <button type="button" className="timer-running-button" disabled>
                                 {formatTimerElapsed(activeLaborTimers[index], timerTick)}
                               </button>
                             ) : (
                               <button
                                 type="button"
                                 onClick={() => startWork(index)}
                                 disabled={isReadOnly || (!item.employeeId && !hasTeamAssignment(item))}
                                 title={!item.employeeId && !hasTeamAssignment(item) ? 'Select employee code or team code first.' : ''}
                               >
                                 Start
                               </button>
                             )}
                             <button
                               type="button"
                               onClick={(event) => finishWork(index, event)}
                               disabled={!activeLaborTimers[index]}
                             >
                               Finish
                             </button>
                           </div>
                         </td>
                       )}
                       <td>
                         <button
                           type="button"
                           className="jobcard-delete-row-button"
                           onClick={() => deleteLabor(index)}
                           disabled={saving || !canDeleteLabor(item, index)}
                         >
                           Delete
                         </button>
                       </td>
                     </tr>
                   ))
                 )}
              </tbody>
            </table>
            <div className="jobcard-row-actions">
              <div className="inventory-search-picker">
                <input
                  type="search"
                  value={laborSearchTerm}
                  onChange={(event) => {
                    setLaborSearchTerm(event.target.value);
                    setSelectedLaborId('');
                  }}
                  placeholder={!isEstimateMode && !selectedServiceEngineerId ? 'Select service engineer first' : 'Search services by name, group, price'}
                  disabled={isReadOnly || (!isEstimateMode && !selectedServiceEngineerId)}
                />
                <select
                  value={selectedLaborId}
                  onChange={(event) => setSelectedLaborId(event.target.value)}
                  disabled={isReadOnly || (!isEstimateMode && !selectedServiceEngineerId)}
                >
                  <option value="">
                    {!isEstimateMode && !selectedServiceEngineerId
                      ? 'Select service engineer first'
                      : 'Select from Services'}
                  </option>
                  {filteredLaborServices.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.jobgroup_name} - {service.description}
                    </option>
                  ))}
                </select>
                {!isEstimateMode && selectedServiceEngineerId && filteredLaborServices.length === 0 && (
                  <small className="vehicle-search-status">No job list found for this service engineer.</small>
                )}
              </div>
              <div className="jobcard-action-buttons">
                <button type="button" onClick={addLaborService} disabled={isReadOnly || (!isEstimateMode && !selectedServiceEngineerId)}>Add selected</button>
              </div>
            </div>

            {!isEstimateMode && (
              <>
                <datalist id="jobcard-employee-options">
                  {assignableEmployees.map((employee) => (
                    <option key={employee.EmployeeId} value={formatEmployeeLookup(employee)} />
                  ))}
                </datalist>
                <datalist id="jobcard-team-options">
                  {activeTeams.map((team) => (
                    <option key={team.JobGroupId} value={formatTeamLookup(team)} />
                  ))}
                </datalist>
              </>
            )}

            <div className="jobcard-attachments">
              <div className="jobcard-attachments-header">
                <div>
                  <h3>{isEstimateMode ? 'Estimate Attachments' : 'Attachments'}</h3>
                  <p>Add images or PDF files for customer reference, approvals, or supporting documents.</p>
                </div>
                {!isReadOnly && (
                  <label className="jobcard-attachment-upload">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleAttachmentSelection}
                      disabled={saving}
                    />
                    Add Image / PDF
                  </label>
                )}
              </div>

              {pendingAttachments.length > 0 && (
                <div className="jobcard-attachment-list pending">
                  {pendingAttachments.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="jobcard-attachment-card">
                      <div className="jobcard-attachment-meta">
                        <span className="jobcard-attachment-type">Queued</span>
                        <strong>{file.name}</strong>
                        <small>{formatFileSize(file.size)}</small>
                      </div>
                      {!isReadOnly && (
                        <button type="button" onClick={() => removePendingAttachment(index)} disabled={saving}>
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {attachments.length > 0 && (
                <div className="jobcard-attachment-list">
                  {attachments.map((attachment) => {
                    const isImage = String(attachment.FileType || '').startsWith('image/');
                    let fileUrl = attachment.FilePath;
                    if (fileUrl && fileUrl.startsWith('/uploads/')) {
                      const baseURL = axios.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || '';
                      fileUrl = `${baseURL}${fileUrl}`;
                    }
                    return (
                      <div key={attachment.AttachmentId} className="jobcard-attachment-card">
                        {isImage ? (
                          <a href={fileUrl} target="_blank" rel="noreferrer" className="jobcard-attachment-preview">
                            <img src={fileUrl} alt={attachment.FileName} />
                          </a>
                        ) : (
                          <a href={fileUrl} target="_blank" rel="noreferrer" className="jobcard-attachment-preview pdf">
                            PDF
                          </a>
                        )}
                        <div className="jobcard-attachment-meta">
                          <span className="jobcard-attachment-type">{isImage ? 'Image' : 'PDF'}</span>
                          <strong>{attachment.FileName}</strong>
                          <small>{formatFileSize(attachment.FileSize)}</small>
                        </div>
                        <div className="jobcard-attachment-actions">
                          <a href={fileUrl} target="_blank" rel="noreferrer">Open</a>
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => deleteAttachment(attachment.AttachmentId)}
                              disabled={saving}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="jobcard-total-line">
              <span>Parts: {money(totalParts)}</span>
              <span>Labor: {money(totalLabor)}</span>
              <strong>Total: {money(totalParts + totalLabor)}</strong>
            </div>

            {!isReadOnly && (
              <div className="jobcard-form-buttons">
                <button className="apply-jobcard-button" type="button" onClick={() => applyJobCard()} disabled={saving}>
                  {saving ? (isEstimateMode ? 'Saving...' : 'Applying...') : (isEstimateMode ? 'Save Estimate' : 'Apply Changes')}
                </button>
                {isEditMode && (
                  <button
                    className="close-jobcard-button"
                    type="button"
                    onClick={closeJobCard}
                    disabled={saving || (!isEstimateMode && hasActiveLaborTimers)}
                    title={!isEstimateMode && hasActiveLaborTimers ? 'Finish active labor timers before closing this job card.' : ''}
                  >
                    {isEstimateMode ? 'Close Estimate' : 'Close Job Card'}
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {message && <div className="jobcard-message">{message}</div>}

        {isAiChatOpen && (
          <div className="jobcard-ai-backdrop" onClick={closeAiChat}>
            <div className="jobcard-ai-modal" onClick={(event) => event.stopPropagation()}>
              <div className="jobcard-ai-header">
                <div>
                  <h2>Albert Einstein</h2>
                  <p>{jobContextSummary}</p>
                </div>
                <button type="button" className="jobcard-ai-close" onClick={closeAiChat}>
                  Close
                </button>
              </div>

              <div className="jobcard-ai-thread">
                {aiMessages.map((item, index) => (
                  <div key={`${item.role}-${index}`} className={`jobcard-ai-message ${item.role}`}>
                    <span>{item.role === 'assistant' ? 'Albert Einstein' : 'You'}</span>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="jobcard-ai-composer">
                <textarea
                  value={aiDraft}
                  onChange={(event) => setAiDraft(event.target.value)}
                  placeholder="Ask for diagnosis ideas, labor steps, parts checks, or customer-ready notes."
                  rows={4}
                  disabled={aiSending}
                />
                <div className="jobcard-ai-actions">
                  <small>Uses current job card car info, KM, parts, labor, and conversation history.</small>
                  <button type="button" onClick={submitAiPrompt} disabled={aiSending || !aiDraft.trim()}>
                    {aiSending ? 'Thinking...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {rejectConfirmIndex !== null && (
          <div className="jobcard-confirm-backdrop" onClick={cancelReject}>
            <div className="jobcard-confirm-modal" onClick={(event) => event.stopPropagation()}>
              <div className="jobcard-confirm-header">
                <h3>Reject Part</h3>
              </div>
              <div className="jobcard-confirm-body">
                <p>Are you sure you want to reject/return this issued part to inventory?</p>
                <p><strong>{parts[rejectConfirmIndex]?.description || parts[rejectConfirmIndex]?.partNumber || 'Unknown Part'}</strong></p>
                <p>Quantity: {parts[rejectConfirmIndex]?.quantity || 0} {parts[rejectConfirmIndex]?.unit || ''}</p>
              </div>
              <div className="jobcard-confirm-actions">
                <button
                  type="button"
                  className="confirm-button-cancel"
                  onClick={cancelReject}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="confirm-button-reject"
                  onClick={confirmReject}
                  disabled={saving}
                >
                  {saving ? 'Rejecting...' : 'Reject Part'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default NewJobCard;
